/* eslint-disable max-len */
import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { Network, ConnectionStatus } from '@capacitor/network';
import { Device } from '@capacitor/device';
import { Toast } from '@capacitor/toast';
import { SplashScreen } from '@capacitor/splash-screen';
import { LOCALSTORAGE_VAR } from 'src/app/models/localstorage';
import {
  ADVANCED_SEARCH_CUSTOM_DEFAULT_COUNT,
  ADVANCED_SEARCH_CUSTOM_LANDSCAPE_TEMPERATURE_DEFAULT,
  ADVANCED_SEARCH_CUSTOM_MAX_COUNT,
  ADVANCED_SEARCH_CUSTOM_MIN_COUNT,
  ADVANCED_SEARCH_CUSTOM_PORTRAIT_TEMPERATURE_DEFAULT,
  ADVANCED_SEARCH_CUSTOM_SYSTEM_PROMPT_MAX_LENGTH,
  ADVANCED_SEARCH_CUSTOM_TEMPERATURE_MAX,
  ADVANCED_SEARCH_CUSTOM_TEMPERATURE_MIN,
  ADVANCED_SEARCH_DEFAULT_RENDERING_MODEL,
  ADVANCED_SEARCH_DEFAULT_PLANNER_MODEL,
  ADVANCED_SEARCH_DEFAULT_GENERATION_MODE,
  AdvancedSearchImageModel,
  AdvancedSearchRenderingModel,
  AdvancedSearchPlannerModel,
  AdvancedSearchGenerationMode,
  normalizeAdvancedSearchRenderingModel,
  normalizeAdvancedSearchPlannerModel,
  normalizeAdvancedSearchGenerationMode,
} from 'src/app/models/advanced-search';
import { LOG_LEVEL_OPTION } from 'src/app/models/common';
import { GradientService } from './gradient.service';
import { ICardLoadProgress, ICardLoadProgressProcessed } from 'src/app/models/card';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  public devicePlatform: string = 'web';

  public skeletonChunkLength: number = 6;
  // public scrollPowerDeccelerationThreshold: number = 500;
  public skeletonChunkLoadDelay: number = 1200;
  public isChunkLoaderShown: boolean = false;
  public cardRenderBufferBackward: number = 1;
  public cardRenderBuffer: number = 2;

  public originalUid: string;
  public customUid: string;

  public animationRatio: number = 0.8;

  public posterBufferForward: number = 6;
  public posterBufferBackward: number = 2;

  // var(--progress-bar-timing)
  public cardStartDelayTime: number = 1800;
  public progressBarHideDelay: number = 1000;

  public wrapperAnimationTime: number = 1150;
  public wrapperAnimationDelay: number = 650;
  public titleAnimationTime: number = 500;
  public titleAnimationDelay: number = 650;
  public descriptionAnimationTime: number =850;
  public descriptionAnimationDelay: number = 950;

  // snackBarAnimation
  public snackBarAppearTiming: number = 350;
  public snackBarHideTiming: number = 200;
  public snackBarAppearScale: number = 0.6;

  public ignoreRenderLimitCheck: boolean = false;


  // Style settings for gradient;

  public showStyleTweaker = false;
  public colors: { base: string; alpha: number }[] = [
    { base: '#000000', alpha: 1 }, // Default to white, fully opaque
    { base: '#000000', alpha: 0 }, // Default to black, fully opaque
  ];
  bezierPoints = [0.48, 0.3, 0.64, 1];
  gradientData = '';
  customPadding = 10;
  customHeight = 100;

  applyTo: string = 'model-feed'; // Default value


  // library section switch

  public showDateModified: boolean = false;

  public showDetailedLogForModelCards: boolean = false;

  // Default AI prompt for single model analysis
  private readonly DEFAULT_AI_PROMPT = `
    Gemini, analyze the 3D model image(s) provided. Focus solely on the main object and its notable details. Do not include any mention of the background or surroundings.

    Respond with ONLY valid JSON in this exact format:
    {
      "short_description": "A catchy, engaging description of the 3D model in maximum 250 characters",
      "technical_description": "A comprehensive technical analysis of the 3D model (minimum 2000 characters) including: object category, design style, geometric complexity, surface materials and textures, color palette, modeling quality, level of detail, topology assessment, potential use cases (gaming, architecture, product visualization, etc.), target audience, technical specifications, and any notable features or characteristics"
    }

    Do NOT include any markdown formatting, code blocks, or additional text outside the JSON structure.
`;

  // Default prompt for collection analysis
  private readonly DEFAULT_COLLECTION_AI_PROMPT = `
    Gemini, analyze the provided collection of 3D model images. Focus on common elements, artistic style, and the cohesive theme that unites these models. Do not include any mention of backgrounds or surroundings.

    Respond with ONLY valid JSON in this exact format:
    {
      "short_description": "A catchy, engaging description of the collection theme in maximum 250 characters",
      "technical_description": "A comprehensive technical analysis of the collection (minimum 2000 characters) including: artistic style, common design elements, color schemes, modeling techniques, level of detail, intended use cases, target audience, and any notable characteristics that unite these models"
    }

    Do NOT include any markdown formatting, code blocks, or additional text outside the JSON structure.
`;

  // Default number of frames to extract from video for AI analysis
  private readonly DEFAULT_AI_FRAMES_COUNT = 2;
  private readonly DEFAULT_AI_TEMPERATURE = 0.4;
  private readonly DEFAULT_AI_THINKING_BUDGET = 4000;
  private readonly DEFAULT_AI_PROVIDER = 'openai';
  private readonly DEFAULT_OPENAI_MODEL = 'gpt-5';
  private readonly DEFAULT_GEMINI_MODEL = 'googleai/gemini-2.5-pro';

  public get logLevelSelector() {
    return this.getLogLevelFromLocalstorage();
  }
  public set logLevelSelector(source: string) {
    this.setLogLevelFromLocalstorage(source);
  }

  public get frameNumberToRender() {
    return this.getFrameNumberFromLocalstorage().toString();
  }
  public set frameNumberToRender(source: string) {
    this.setFrameNumberFromLocalstorage(source);
  }

  public get additionalSettingsEnabled() {
    return this.getAdditionalSettingsEnabled();
  }

  public set additionalSettingsEnabled(source: boolean) {
    this.setAdditionalSettingsEnabled(source);
  }

  public get aiPrompt() {
    return this.getAiPromptFromLocalstorage();
  }

  public set aiPrompt(source: string) {
    this.setAiPromptToLocalstorage(source);
  }

  public get aiFramesCount() {
    return this.getAiFramesCountFromLocalstorage();
  }

  public set aiFramesCount(source: number) {
    this.setAiFramesCountToLocalstorage(source);
  }

  public get aiTemperature() {
    return this.getAiTemperatureFromLocalstorage();
  }

  public set aiTemperature(source: number) {
    this.setAiTemperatureToLocalstorage(source);
  }

  public get aiProvider(): 'gemini' | 'openai' {
    return this.getAiProviderFromLocalstorage();
  }

  public set aiProvider(source: 'gemini' | 'openai') {
    this.setAiProviderToLocalstorage(source);
  }

  public get aiOpenAIModel(): string {
    return this.getAiOpenAIModelFromLocalstorage();
  }

  public set aiOpenAIModel(source: string) {
    this.setAiOpenAIModelToLocalstorage(source);
  }

  public get aiGeminiModel(): string {
    return this.getAiGeminiModelFromLocalstorage();
  }

  public set aiGeminiModel(source: string) {
    this.setAiGeminiModelToLocalstorage(source);
  }

  public get aiThinkingBudget(): number {
    return this.getAiThinkingBudgetFromLocalstorage();
  }

  public set aiThinkingBudget(source: number) {
    this.setAiThinkingBudgetToLocalstorage(source);
  }

  public get advancedSearchGenerationMode(): AdvancedSearchGenerationMode {
    return this.getAdvancedSearchGenerationModeFromLocalstorage();
  }

  public set advancedSearchGenerationMode(source: AdvancedSearchGenerationMode) {
    this.setAdvancedSearchGenerationModeToLocalstorage(source);
  }

  public get advancedSearchPairPreviewDebugMode(): boolean {
    return this.getAdvancedSearchPairPreviewDebugModeFromLocalstorage();
  }

  public set advancedSearchPairPreviewDebugMode(source: boolean) {
    this.setAdvancedSearchPairPreviewDebugModeToLocalstorage(source);
  }

  public get advancedSearchRenderingModel(): AdvancedSearchRenderingModel {
    return this.getAdvancedSearchRenderingModelFromLocalstorage();
  }

  public set advancedSearchRenderingModel(source: AdvancedSearchRenderingModel) {
    this.setAdvancedSearchRenderingModelToLocalstorage(source);
  }

  // Backward compatible alias for legacy callers.
  public get advancedSearchPairImageModel(): AdvancedSearchImageModel {
    return this.advancedSearchRenderingModel;
  }

  // Backward compatible alias for legacy callers.
  public set advancedSearchPairImageModel(source: AdvancedSearchImageModel) {
    this.advancedSearchRenderingModel = source;
  }

  public get advancedSearchPlannerModel(): AdvancedSearchPlannerModel {
    return this.getAdvancedSearchPlannerModelFromLocalstorage();
  }

  public set advancedSearchPlannerModel(source: AdvancedSearchPlannerModel) {
    this.setAdvancedSearchPlannerModelToLocalstorage(source);
  }

  public get advancedSearchCustomAssetCount(): number {
    return this.getAdvancedSearchCustomAssetCountFromLocalstorage();
  }

  public set advancedSearchCustomAssetCount(source: number) {
    this.setAdvancedSearchCustomAssetCountToLocalstorage(source);
  }

  public get advancedSearchCustomSystemPrompt(): string {
    return this.getAdvancedSearchCustomSystemPromptFromLocalstorage();
  }

  public set advancedSearchCustomSystemPrompt(source: string) {
    this.setAdvancedSearchCustomSystemPromptToLocalstorage(source);
  }

  public get advancedSearchCustomPortraitTemperature(): number {
    return this.getAdvancedSearchCustomPortraitTemperatureFromLocalstorage();
  }

  public set advancedSearchCustomPortraitTemperature(source: number) {
    this.setAdvancedSearchCustomPortraitTemperatureToLocalstorage(source);
  }

  public get advancedSearchCustomLandscapeTemperature(): number {
    return this.getAdvancedSearchCustomLandscapeTemperatureFromLocalstorage();
  }

  public set advancedSearchCustomLandscapeTemperature(source: number) {
    this.setAdvancedSearchCustomLandscapeTemperatureToLocalstorage(source);
  }

  public backBtnEventDelayTime: number = 50;
  public discoverBtnEventDelayTime: number = 50;
  public homeBtnEventDelayTime: number = 50;

  public collectionDetailsScrollSpeed: number = 2500;



  public isSplashHidden: boolean = false;
  public videoResizeType: string = 'cover';

  public showServicePageAndShowDebugDataDataOnCard: boolean;
  public networkStatus: ConnectionStatus = {
    connected: true,
    connectionType: 'unknown'
  }

  constructor(
    public gradientService: GradientService
  ) {


    this.customUid = localStorage.getItem(LOCALSTORAGE_VAR.CUSTOM_UID);
    // DISABLE CLEAR CUSTOM UID;
    this.clearCustomUidInLocalStorage();

    Device.getInfo().then((deviceInfo) => {
      this.devicePlatform = deviceInfo.platform;
    });

    Network.getStatus().then((firstStatus) => {
      this.networkStatus = firstStatus
      console.log('*NETWORK STATUS*: ', this.networkStatus)
    });

    Network.addListener('networkStatusChange', changedStatus => {
      console.log('Network status changed', changedStatus);
      Network.getStatus().then((status) => {
        this.networkStatus = status
        console.log('*NETWORK STATUS*: ', this.networkStatus)
      });
    });

    // this.showServicePageAndShowDebugDataDataOnCard = environment.showServicePageAndShowDebugData ? true : false;
    this.showServicePageAndShowDebugDataDataOnCard = false;

    const collectionDetailsGrad = 'linear-gradient(to top, hsl(0, 0%, 0%), hsla(0, 0%, 0%, 0.93933) 9.09%, hsla(0, 0%, 0%, 0.8709) 17.63%, hsla(0, 0%, 0%, 0.79631) 25.65%, hsla(0, 0%, 0%, 0.71719) 33.2%, hsla(0, 0%, 0%, 0.63513) 40.31%, hsla(0, 0%, 0%, 0.55176) 47.02%, hsla(0, 0%, 0%, 0.46868) 53.36%, hsla(0, 0%, 0%, 0.3875) 59.38%, hsla(0, 0%, 0%, 0.30984) 65.09%, hsla(0, 0%, 0%, 0.2373) 70.56%, hsla(0, 0%, 0%, 0.17151) 75.8%, hsla(0, 0%, 0%, 0.11406) 80.86%, hsla(0, 0%, 0%, 0.06658) 85.77%, hsla(0, 0%, 0%, 0.03066) 90.58%, hsla(0, 0%, 0%, 0.00793) 95.31%, hsla(0, 0%, 0%, 0))'

    const modelTopGradientData = 'linear-gradient(to top, hsla(0, 0%, 0%, 0.85), hsla(0, 0%, 0%, 0.79843) 9.09%, hsla(0, 0%, 0%, 0.74026) 17.63%, hsla(0, 0%, 0%, 0.67687) 25.65%, hsla(0, 0%, 0%, 0.60961) 33.2%, hsla(0, 0%, 0%, 0.53986) 40.31%, hsla(0, 0%, 0%, 0.46899) 47.02%, hsla(0, 0%, 0%, 0.39838) 53.36%, hsla(0, 0%, 0%, 0.32937) 59.38%, hsla(0, 0%, 0%, 0.26336) 65.09%, hsla(0, 0%, 0%, 0.20171) 70.56%, hsla(0, 0%, 0%, 0.14578) 75.8%, hsla(0, 0%, 0%, 0.09695) 80.86%, hsla(0, 0%, 0%, 0.05659) 85.77%, hsla(0, 0%, 0%, 0.02606) 90.58%, hsla(0, 0%, 0%, 0.00674) 95.31%, hsla(0, 0%, 0%, 0))'

    window.document.documentElement.style.setProperty('--customgradientfeed', modelTopGradientData);
    window.document.documentElement.style.setProperty('--customtoppadding', this.customPadding + 'px');
    window.document.documentElement.style.setProperty('--customgradientdetails', collectionDetailsGrad);
    window.document.documentElement.style.setProperty('--customheight', 100 + '%');

   }

   showToastMsg(text): void {
    Toast.show({
      text , duration: 'long', position: 'top'
    });
   }

   hideSplashScreen(): void {
    if(this.isSplashHidden === false ) {
      this.isSplashHidden = true;
      SplashScreen.hide({fadeOutDuration: 350});
      console.log('PERF_ Splash hidden:', performance.now())
    }
   }

   setCustomUidInLocalstorage(value: string): void {
    localStorage.setItem(LOCALSTORAGE_VAR.CUSTOM_UID, value.toString());
   }

   getCustomUidInLocalStorage(): string {
    const customUid = localStorage.getItem(LOCALSTORAGE_VAR.CUSTOM_UID);
    return customUid ? customUid : '';
   }

   clearCustomUidInLocalStorage(): void {
    localStorage.removeItem(LOCALSTORAGE_VAR.CUSTOM_UID);
   }

  setLogLevelFromLocalstorage(source: string) {
    localStorage.setItem(LOCALSTORAGE_VAR.LOG_LEVEL, source.toString());
  }
  getLogLevelFromLocalstorage(): string {
    const logLevel = localStorage.getItem(LOCALSTORAGE_VAR.LOG_LEVEL);
    return logLevel ? logLevel : LOG_LEVEL_OPTION.PROD_MODE;
  }

  getOriginalUidFromLocalstorage(): string {
    return localStorage.getItem(LOCALSTORAGE_VAR.ORIGINAL_UID);
   }


  setFrameNumberFromLocalstorage(frameNumber: string) {
    localStorage.setItem(LOCALSTORAGE_VAR.FRAME_NUMBER, frameNumber);
  }
  getFrameNumberFromLocalstorage(): number {
    const rawFrameNumber = localStorage.getItem(LOCALSTORAGE_VAR.FRAME_NUMBER);
    let processedFrameNumber
    if(rawFrameNumber) {
      processedFrameNumber = parseInt(rawFrameNumber, 10);
    }
    return processedFrameNumber ? processedFrameNumber : 900;
  }
  // The count someone set by hand (service settings, or `frameNumber` in localStorage), or null
  // when nothing usable is stored — unlike the getter above, it does not fall back to 900.
  getStoredFrameNumber(): number | null {
    const frameNumber = parseInt(localStorage.getItem(LOCALSTORAGE_VAR.FRAME_NUMBER), 10);
    return frameNumber > 0 ? frameNumber : null;
  }

  setAdditionalSettingsEnabled(value: boolean): void {
    localStorage.setItem(LOCALSTORAGE_VAR.ADDITIONAL_SETTINGS_ENABLED, value.toString());
  }

  getAdditionalSettingsEnabled(): boolean {
    const additionalSettingsEnabled = localStorage.getItem(LOCALSTORAGE_VAR.ADDITIONAL_SETTINGS_ENABLED);
    return additionalSettingsEnabled && additionalSettingsEnabled === 'true' ? true : false
  }

  getAiPromptFromLocalstorage(): string {
    const aiPrompt = localStorage.getItem(LOCALSTORAGE_VAR.AI_PROMPT);
    return aiPrompt ? aiPrompt : this.DEFAULT_AI_PROMPT;
  }

  setAiPromptToLocalstorage(source: string): void {
    localStorage.setItem(LOCALSTORAGE_VAR.AI_PROMPT, source);
  }

  getDefaultAiPrompt(): string {
    return this.DEFAULT_AI_PROMPT;
  }

  getDefaultCollectionAiPrompt(): string {
    return this.DEFAULT_COLLECTION_AI_PROMPT;
  }

  resetAiPromptToDefault(): void {
    this.setAiPromptToLocalstorage(this.DEFAULT_AI_PROMPT);
  }

  getAiFramesCountFromLocalstorage(): number {
    const framesCount = localStorage.getItem(LOCALSTORAGE_VAR.AI_FRAMES_COUNT);
    return framesCount ? parseInt(framesCount, 10) : this.DEFAULT_AI_FRAMES_COUNT;
  }

  setAiFramesCountToLocalstorage(source: number): void {
    localStorage.setItem(LOCALSTORAGE_VAR.AI_FRAMES_COUNT, source.toString());
  }

  getDefaultAiFramesCount(): number {
    return this.DEFAULT_AI_FRAMES_COUNT;
  }

  resetAiFramesCountToDefault(): void {
    this.setAiFramesCountToLocalstorage(this.DEFAULT_AI_FRAMES_COUNT);
  }

  getAiTemperatureFromLocalstorage(): number {
    const temperature = localStorage.getItem(LOCALSTORAGE_VAR.AI_TEMPERATURE);
    return temperature ? parseFloat(temperature) : this.DEFAULT_AI_TEMPERATURE;
  }

  setAiTemperatureToLocalstorage(source: number): void {
    localStorage.setItem(LOCALSTORAGE_VAR.AI_TEMPERATURE, source.toString());
  }

  getDefaultAiTemperature(): number {
    return this.DEFAULT_AI_TEMPERATURE;
  }

  resetAiTemperatureToDefault(): void {
    this.setAiTemperatureToLocalstorage(this.DEFAULT_AI_TEMPERATURE);
  }

  getAiProviderFromLocalstorage(): 'gemini' | 'openai' {
    const provider = localStorage.getItem(LOCALSTORAGE_VAR.AI_PROVIDER);
    if (provider === 'gemini' || provider === 'openai') {
      return provider;
    }
    return this.DEFAULT_AI_PROVIDER;
  }

  setAiProviderToLocalstorage(source: 'gemini' | 'openai'): void {
    localStorage.setItem(LOCALSTORAGE_VAR.AI_PROVIDER, source);
  }

  getAiOpenAIModelFromLocalstorage(): string {
    const model = localStorage.getItem(LOCALSTORAGE_VAR.AI_OPENAI_MODEL);
    if (model && model.trim().length > 0) {
      return model;
    }
    return this.DEFAULT_OPENAI_MODEL;
  }

  setAiOpenAIModelToLocalstorage(source: string): void {
    localStorage.setItem(LOCALSTORAGE_VAR.AI_OPENAI_MODEL, source);
  }

  getAiGeminiModelFromLocalstorage(): string {
    const model = localStorage.getItem(LOCALSTORAGE_VAR.AI_GEMINI_MODEL);
    if (model && model.trim().length > 0) {
      return model;
    }
    return this.DEFAULT_GEMINI_MODEL;
  }

  setAiGeminiModelToLocalstorage(source: string): void {
    localStorage.setItem(LOCALSTORAGE_VAR.AI_GEMINI_MODEL, source);
  }

  getAiThinkingBudgetFromLocalstorage(): number {
    const thinkingBudget = localStorage.getItem(LOCALSTORAGE_VAR.AI_THINKING_BUDGET);
    return thinkingBudget ? parseInt(thinkingBudget, 10) : this.DEFAULT_AI_THINKING_BUDGET;
  }

  setAiThinkingBudgetToLocalstorage(source: number): void {
    localStorage.setItem(LOCALSTORAGE_VAR.AI_THINKING_BUDGET, source.toString());
  }

  getAdvancedSearchGenerationModeFromLocalstorage(): AdvancedSearchGenerationMode {
    const mode = normalizeAdvancedSearchGenerationMode(
      localStorage.getItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_GENERATION_MODE),
    );

    return mode || ADVANCED_SEARCH_DEFAULT_GENERATION_MODE;
  }

  setAdvancedSearchGenerationModeToLocalstorage(source: AdvancedSearchGenerationMode): void {
    const normalizedMode = normalizeAdvancedSearchGenerationMode(source) || ADVANCED_SEARCH_DEFAULT_GENERATION_MODE;
    localStorage.setItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_GENERATION_MODE, normalizedMode);
  }

  getAdvancedSearchGenerationModeValidationState(): {
    mode: AdvancedSearchGenerationMode;
    isValid: boolean;
    rawMode: string | null;
  } {
    const rawMode = localStorage.getItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_GENERATION_MODE);
    const normalizedMode = normalizeAdvancedSearchGenerationMode(rawMode);
    const isValid = rawMode === null || normalizedMode !== null;

    return {
      mode: normalizedMode || ADVANCED_SEARCH_DEFAULT_GENERATION_MODE,
      isValid,
      rawMode,
    };
  }

  getAdvancedSearchPairPreviewDebugModeFromLocalstorage(): boolean {
    return localStorage.getItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_PAIR_PREVIEW_DEBUG_MODE) === 'true';
  }

  setAdvancedSearchPairPreviewDebugModeToLocalstorage(source: boolean): void {
    localStorage.setItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_PAIR_PREVIEW_DEBUG_MODE, source ? 'true' : 'false');
  }

  getAdvancedSearchRenderingModelFromLocalstorage(): AdvancedSearchRenderingModel {
    const model = normalizeAdvancedSearchRenderingModel(
      localStorage.getItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_PAIR_IMAGE_MODEL),
    );
    return model || ADVANCED_SEARCH_DEFAULT_RENDERING_MODEL;
  }

  setAdvancedSearchRenderingModelToLocalstorage(source: AdvancedSearchRenderingModel): void {
    const normalizedModel = normalizeAdvancedSearchRenderingModel(source) || ADVANCED_SEARCH_DEFAULT_RENDERING_MODEL;
    localStorage.setItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_PAIR_IMAGE_MODEL, normalizedModel);
  }

  // Backward compatible aliases for internal/external legacy method calls.
  getAdvancedSearchPairImageModelFromLocalstorage(): AdvancedSearchImageModel {
    return this.getAdvancedSearchRenderingModelFromLocalstorage();
  }

  setAdvancedSearchPairImageModelToLocalstorage(source: AdvancedSearchImageModel): void {
    this.setAdvancedSearchRenderingModelToLocalstorage(source);
  }

  getAdvancedSearchPlannerModelFromLocalstorage(): AdvancedSearchPlannerModel {
    const model = normalizeAdvancedSearchPlannerModel(
      localStorage.getItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_PLANNER_MODEL),
    );
    return model || ADVANCED_SEARCH_DEFAULT_PLANNER_MODEL;
  }

  setAdvancedSearchPlannerModelToLocalstorage(source: AdvancedSearchPlannerModel): void {
    const normalizedModel = normalizeAdvancedSearchPlannerModel(source) || ADVANCED_SEARCH_DEFAULT_PLANNER_MODEL;
    localStorage.setItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_PLANNER_MODEL, normalizedModel);
  }

  getAdvancedSearchCustomAssetCountFromLocalstorage(): number {
    const rawCount = localStorage.getItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_CUSTOM_ASSET_COUNT);
    const parsedCount = Number(rawCount);
    if (Number.isNaN(parsedCount)) {
      return ADVANCED_SEARCH_CUSTOM_DEFAULT_COUNT;
    }

    return Math.max(
      ADVANCED_SEARCH_CUSTOM_MIN_COUNT,
      Math.min(ADVANCED_SEARCH_CUSTOM_MAX_COUNT, Math.floor(parsedCount)),
    );
  }

  setAdvancedSearchCustomAssetCountToLocalstorage(source: number): void {
    const parsedCount = Number(source);
    const normalizedCount = Number.isNaN(parsedCount) ?
      ADVANCED_SEARCH_CUSTOM_DEFAULT_COUNT :
      Math.max(
        ADVANCED_SEARCH_CUSTOM_MIN_COUNT,
        Math.min(ADVANCED_SEARCH_CUSTOM_MAX_COUNT, Math.floor(parsedCount)),
      );

    localStorage.setItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_CUSTOM_ASSET_COUNT, `${normalizedCount}`);
  }

  getAdvancedSearchCustomSystemPromptFromLocalstorage(): string {
    const rawPrompt = localStorage.getItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_CUSTOM_SYSTEM_PROMPT) || '';
    return `${rawPrompt}`.trim().slice(0, ADVANCED_SEARCH_CUSTOM_SYSTEM_PROMPT_MAX_LENGTH);
  }

  setAdvancedSearchCustomSystemPromptToLocalstorage(source: string): void {
    const normalizedPrompt = `${source || ''}`.trim().slice(0, ADVANCED_SEARCH_CUSTOM_SYSTEM_PROMPT_MAX_LENGTH);
    localStorage.setItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_CUSTOM_SYSTEM_PROMPT, normalizedPrompt);
  }

  private normalizeAdvancedSearchCustomTemperature(source: number, fallback: number): number {
    const parsedTemperature = Number(source);
    if (!Number.isFinite(parsedTemperature)) {
      return fallback;
    }

    return Math.max(
      ADVANCED_SEARCH_CUSTOM_TEMPERATURE_MIN,
      Math.min(ADVANCED_SEARCH_CUSTOM_TEMPERATURE_MAX, parsedTemperature),
    );
  }

  getAdvancedSearchCustomPortraitTemperatureFromLocalstorage(): number {
    const rawTemperature = localStorage.getItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_CUSTOM_PORTRAIT_TEMPERATURE);
    if (rawTemperature === null) {
      return ADVANCED_SEARCH_CUSTOM_PORTRAIT_TEMPERATURE_DEFAULT;
    }

    return this.normalizeAdvancedSearchCustomTemperature(
      Number(rawTemperature),
      ADVANCED_SEARCH_CUSTOM_PORTRAIT_TEMPERATURE_DEFAULT,
    );
  }

  setAdvancedSearchCustomPortraitTemperatureToLocalstorage(source: number): void {
    const normalizedTemperature = this.normalizeAdvancedSearchCustomTemperature(
      source,
      ADVANCED_SEARCH_CUSTOM_PORTRAIT_TEMPERATURE_DEFAULT,
    );
    localStorage.setItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_CUSTOM_PORTRAIT_TEMPERATURE, `${normalizedTemperature}`);
  }

  getAdvancedSearchCustomLandscapeTemperatureFromLocalstorage(): number {
    const rawTemperature = localStorage.getItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_CUSTOM_LANDSCAPE_TEMPERATURE);
    if (rawTemperature === null) {
      return ADVANCED_SEARCH_CUSTOM_LANDSCAPE_TEMPERATURE_DEFAULT;
    }

    return this.normalizeAdvancedSearchCustomTemperature(
      Number(rawTemperature),
      ADVANCED_SEARCH_CUSTOM_LANDSCAPE_TEMPERATURE_DEFAULT,
    );
  }

  setAdvancedSearchCustomLandscapeTemperatureToLocalstorage(source: number): void {
    const normalizedTemperature = this.normalizeAdvancedSearchCustomTemperature(
      source,
      ADVANCED_SEARCH_CUSTOM_LANDSCAPE_TEMPERATURE_DEFAULT,
    );
    localStorage.setItem(LOCALSTORAGE_VAR.ADVANCED_SEARCH_CUSTOM_LANDSCAPE_TEMPERATURE, `${normalizedTemperature}`);
  }

  getDefaultAiThinkingBudget(): number {
    return this.DEFAULT_AI_THINKING_BUDGET;
  }

  resetAiThinkingBudgetToDefault(): void {
    this.setAiThinkingBudgetToLocalstorage(this.DEFAULT_AI_THINKING_BUDGET);
  }

  getAiPromptForFramesCount(framesCount: number, isCollection: boolean = false): string {
    const baseInstruction = isCollection ?
      'Gemini, analyze the provided collection of images from 3D model videos.' :
      'Gemini, analyze the frames from this 3D model video.';

    let frameDescription = '';
    switch (framesCount) {
      case 1:
        frameDescription = 'You will receive 1 frame from the beginning of the video showing the initial view of the 3D model.';
        break;
      case 2:
        frameDescription = 'You will receive 2 frames: the first frame from the beginning and one frame from the middle of the video rotation, showing different angles of the 3D model.';
        break;
      case 3:
        frameDescription = 'You will receive 3 frames: from the beginning, first third, and second third of the video rotation, showing multiple angles of the 3D model.';
        break;
      case 4:
        frameDescription = 'You will receive 4 frames: from 0%, 25%, 50%, and 75% of the video duration, showing a comprehensive view of the 3D model from different angles.';
        break;
      default:
        frameDescription = 'You will receive multiple frames from different angles of the 3D model rotation.';
    }

    const responseFormat = isCollection ?
      `Respond with ONLY valid JSON in this exact format:
{
  "short_description": "A catchy, engaging description of the collection theme in maximum 250 characters",
  "technical_description": "A comprehensive technical analysis of the collection (minimum 2000 characters) including: artistic style, common design elements, color schemes, modeling techniques, level of detail, intended use cases, target audience, and any notable characteristics that unite these models"
}` :
      `Respond with ONLY valid JSON in this exact format:
{
  "short_description": "A catchy, engaging description of the 3D model in maximum 250 characters",
  "technical_description": "A comprehensive technical analysis of the 3D model (minimum 2000 characters) including: object category, design style, geometric complexity, surface materials and textures, color palette, modeling quality, level of detail, topology assessment, potential use cases (gaming, architecture, product visualization, etc.), target audience, technical specifications, and any notable features or characteristics"
}`;

    const focusInstruction = isCollection ?
      'Focus on common elements, artistic style, and the cohesive theme that unites these models. Do not include any mention of backgrounds or surroundings.' :
      'Focus solely on the main object and its notable details. Do not include any mention of the background or surroundings.';

    return `${baseInstruction} ${frameDescription} ${focusInstruction} ${responseFormat}

Do NOT include any markdown formatting, code blocks, or additional text outside the JSON structure.`;
  }


  // STYLE SETTINGS FOR GRADIENT

  updateStyles() {
    console.log(this.bezierPoints);
    const coordinates = this.calculateBezierCoordinates(this.bezierPoints, 16);  // Generate 16 coordinates

    // Convert colors with transparency to RGBA strings
    const rgbaColors = this.colors.map(color => {
      const rgb = this.hexToRgb(color.base);
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${color.alpha})`;
    });

    const gradient = this.gradientService.generateColorStops(rgbaColors, coordinates, 5, 'hsl');
    this.gradientData = `linear-gradient(to top, ${gradient.join(', ')})`;
    this.applyGradient();
  }

  // Assuming the hexToRgb function is available in the service
  // And this.colors is an array of { base: string, alpha: number }


  applyGradient() {
    // Make sure this.gradientData is formatted correctly

    // Set the --customgradientfeed variable on the root element
    if (this.applyTo === 'model-feed') {
      window.document.documentElement.style.setProperty('--customgradientfeed', this.gradientData);
      window.document.documentElement.style.setProperty('--customtoppadding', this.customPadding + 'px');
    }
    if(this.applyTo === 'collection-details') {
      window.document.documentElement.style.setProperty('--customgradientdetails', this.gradientData);
      window.document.documentElement.style.setProperty('--customheight', this.customHeight + '%');

    }

    console.log('Applied gradient:', this.gradientData);
  }

  getRgbaString(color: { base: string; alpha: number }): string {
    const rgb = this.hexToRgb(color.base); // You'll need to convert hex color to rgb
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${color.alpha})`;
  }

  // Utility function to convert hex to RGB
  public hexToRgb(hex: string) {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }


  public calculateBezierCoordinates(points: number[], steps: number): { x: number; y: number }[] {
    const coordinates = [];

    // Extract control points
    const [p0, p1, p2, p3] = [
      { x: 0, y: 0 }, // Start point is always (0, 0) for CSS transitions
      { x: points[0], y: points[1] }, // First control point
      { x: points[2], y: points[3] }, // Second control point
      { x: 1, y: 1 }  // End point is always (1, 1) for CSS transitions
    ];

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = this.computeBezier(t, p0.x, p1.x, p2.x, p3.x);
      const y = this.computeBezier(t, p0.y, p1.y, p2.y, p3.y);
      coordinates.push({ x, y });
    }

    return coordinates;
  }

  private computeBezier(t: number, p0: number, p1: number, p2: number, p3: number): number {
    return Math.pow(1 - t, 3) * p0 +
           3 * Math.pow(1 - t, 2) * t * p1 +
           3 * (1 - t) * Math.pow(t, 2) * p2 +
           Math.pow(t, 3) * p3;
  }


    public processCardStats(cardProgressStats: ICardLoadProgress[]): ICardLoadProgressProcessed[] {
      const statsProcessed: ICardLoadProgressProcessed[] = [];
      cardProgressStats.forEach((cardStat) => {
        const cardProgressStat: ICardLoadProgressProcessed = {
          cardNameShort: '',
          timeToStartPlaying: 0,
          timeToFullLoad: 0,
          percentOfVideoToStartPlaying: 0,
        };
        // PROCESS NAME TO BE REALLY SHORT;
        const nameStartPos = cardStat.cardName.indexOf('/') + 1;
        const shortName = cardStat.cardName.substring(nameStartPos,nameStartPos + 2);

        cardProgressStat.cardNameShort = shortName
        cardStat.progress.forEach((progress) => {
          if(progress.eventName === 'CardCanPlayThrough') {
            cardProgressStat.timeToStartPlaying = progress.time;
            cardProgressStat.percentOfVideoToStartPlaying = progress.progressMark;
          }
          if(progress.progressMark === 1) {
            cardProgressStat.timeToFullLoad = progress.time;
          }
        })

        statsProcessed.push(cardProgressStat);
      })
      return statsProcessed
    }

}
